import React, { useState, useRef } from 'react';
import { Popper, Paper, MenuList, ClickAwayListener } from '@mui/material';
import PropTypes from 'prop-types';

const PopperSelect = ({
  value,
  onChange,
  multiple = false,
  displayEmpty = false,
  renderValue,
  children,
  MenuProps = {},
  onClose,
  closeSignal,
  disabled = false,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const [localValue, setLocalValue] = useState(value);

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const previousCloseSignalRef = React.useRef(closeSignal);
  React.useEffect(() => {
    if (previousCloseSignalRef.current !== closeSignal) {
      previousCloseSignalRef.current = closeSignal;
      setOpen(false);
    }
  }, [closeSignal]);

  const handleToggle = (e) => {
    if (disabled) return;
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    if (onClose) onClose(event);
  };

  const handleItemClick = (childValue, event) => {
    if (multiple) {
      const currentValues = Array.isArray(localValue) ? localValue : [];
      let newValues;
      // Simple toggle logic for multiple select
      if (currentValues.includes(childValue)) {
        newValues = currentValues.filter(v => v !== childValue);
      } else {
        newValues = [...currentValues, childValue];
      }
      setLocalValue(newValues);
      // Defer the onChange callback to allow the dropdown selection UI to update instantly
      setTimeout(() => {
        if (onChange) {
          onChange({ target: { value: newValues } });
        }
      }, 0);
    } else {
      setLocalValue(childValue);
      setOpen(false);
      if (onClose) onClose(event);
      // Defer the onChange callback to allow the dropdown to close and visually select the item instantly
      setTimeout(() => {
        if (onChange) {
          onChange({ target: { value: childValue } });
        }
      }, 0);
    }
  };

  const mappedChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    
    const childValue = child.props.value;
    
    let isSelected = false;
    if (multiple) {
      isSelected = Array.isArray(localValue) ? localValue.includes(childValue) : false;
    } else {
      isSelected = localValue === childValue;
    }

    const originalOnClick = child.props.onClick;
    const onClick = (e) => {
      if (child.props.disabled) return;
      
      let preventSelection = false;
      
      if (originalOnClick) {
         originalOnClick(e);
         // If a custom click handler prevents default or stops propagation, respect it.
         // e.isDefaultPrevented() is available on React synthetic events.
         if (e.isDefaultPrevented()) {
           preventSelection = true;
         }
      }
      
      if (!preventSelection && childValue !== undefined) {
         handleItemClick(childValue, e);
      }
    };

    return React.cloneElement(child, {
      selected: isSelected,
      onClick,
    });
  });

  let displayValue = '';
  if (renderValue) {
    displayValue = renderValue(localValue);
  } else {
    displayValue = Array.isArray(localValue) ? localValue.join(', ') : String(localValue || '');
    if (!displayValue && displayEmpty) {
       displayValue = 'Select...';
    }
  }

  const { PaperProps, anchorOrigin, transformOrigin, marginThreshold, ...otherMenuProps } = MenuProps;
  
  // To keep CSS matching exact with MUI Select
  return (
    <>
      <div
        ref={anchorRef}
        onClick={handleToggle}
        className={`MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-formControl MuiInputBase-sizeSmall ${disabled ? 'Mui-disabled' : ''} ${open ? 'Mui-focused' : ''}`}
        style={{ 
          cursor: disabled ? 'default' : 'pointer', 
          pointerEvents: disabled ? 'none' : 'auto',
          opacity: disabled ? 0.6 : 1,
          width: '100%', 
          position: 'relative' 
        }}
        {...props}
      >
        <div 
          role="combobox" 
          aria-expanded={open} 
          aria-controls={open ? 'menu-list-grow' : undefined}
          className={`MuiSelect-select MuiSelect-outlined MuiInputBase-input MuiOutlinedInput-input MuiInputBase-inputSizeSmall ${disabled ? 'Mui-disabled' : ''}`}
        >
          {displayValue}
        </div>
        <fieldset 
          aria-hidden="true" 
          className="MuiOutlinedInput-notchedOutline"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            top: 0,
            margin: 0,
            padding: 0,
            pointerEvents: 'none',
            borderRadius: 'inherit',
            borderStyle: 'solid',
            borderWidth: '1px'
          }}
        >
          <legend style={{ display: 'none' }}></legend>
        </fieldset>
      </div>

      {(() => {
        const cleanPaperStyle = { ...PaperProps?.style };
        if (cleanPaperStyle.width === 1) {
          delete cleanPaperStyle.width;
        }

        return (
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            disablePortal={false}
            style={{ 
              zIndex: 1500, 
              width: cleanPaperStyle.width || anchorRef.current?.clientWidth
            }}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, 4],
                },
              },
              {
                name: 'flip',
                enabled: true,
                options: {
                  boundary: 'viewport',
                },
              },
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  altAxis: true,
                  altBoundary: true,
                  tether: true,
                  boundary: 'viewport',
                },
              },
            ]}
            {...otherMenuProps}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <Paper 
                elevation={8} 
                className={PaperProps?.className}
                style={{ 
                  maxHeight: 250, 
                  overflow: 'auto', 
                  ...cleanPaperStyle 
                }}
              >
                <MenuList autoFocusItem={open} id="menu-list-grow">
                  {mappedChildren}
                </MenuList>
              </Paper>
            </ClickAwayListener>
          </Popper>
        );
      })()}
    </>
  );
};

PopperSelect.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  multiple: PropTypes.bool,
  displayEmpty: PropTypes.bool,
  renderValue: PropTypes.func,
  children: PropTypes.node,
  MenuProps: PropTypes.object,
  onClose: PropTypes.func,
  closeSignal: PropTypes.any,
  disabled: PropTypes.bool,
};

export default PopperSelect;
